import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { LogOut, QrCode, Home, User, LogIn, UserPlus } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import Button from './Button';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut, loading } = useAuth();

  // Get user role
  const userRole = user?.user_metadata?.role;
  const isBusinessUser = userRole === 'business';

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/homepage');
    } catch (error) {
      console.error('Error signing out:', error);
      navigate('/homepage');
    }
  };

  // Handle protected route clicks for non-authenticated users
  const handleProtectedRouteClick = (e, route) => {
    if (!user) {
      e?.preventDefault();
      navigate('/signin', { state: { from: { pathname: route } } });
    }
  };

  // Don't show header on auth pages
  if (location?.pathname === '/signin' ||
  location?.pathname === '/signup') {
    return null;
  }

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-lg border-b border-green-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <div className="flex items-center">
            <Link to="/homepage" className="flex items-center group">
              <div className="relative">
                <img
                  src="/foodloop/assets/images/slazzer-preview-av4u0-1762512006110.png"
                  alt="Food Loop Logo"
                  className="h-16 w-auto transition-transform group-hover:scale-105" />

                <div className="absolute -inset-2 bg-gradient-to-r from-green-500 to-blue-500 rounded-full opacity-0 group-hover:opacity-20 transition-opacity blur-xl"></div>
              </div>
            </Link>
          </div>

          <nav className="hidden md:flex items-center space-x-1">
            <Link
              to="/homepage"
              className="flex items-center px-4 py-2 rounded-xl text-gray-700 hover:text-green-600 hover:bg-gradient-to-r hover:from-green-50 hover:to-green-100 font-medium transition-all duration-200">

              <Home className="h-4 w-4 mr-2" />
              Inicio
            </Link>
            
            {/* Dashboard - Hidden for business users */}
            {!isBusinessUser &&
            <Link
              to="/consumer-dashboard"
              onClick={(e) => handleProtectedRouteClick(e, '/consumer-dashboard')}
              className={`px-4 py-2 rounded-xl font-medium transition-all duration-200 ${
              user ?
              "text-gray-700 hover:text-green-600 hover:bg-gradient-to-r hover:from-green-50 hover:to-green-100" : "text-gray-400 hover:text-red-600 hover:bg-gradient-to-r hover:from-red-50 hover:to-red-100 cursor-pointer"}`
              }
              title={!user ? "Inicia sesión para acceder al Dashboard" : "Dashboard"}>

                Dashboard {!user && "🔒"}
              </Link>
            }
            
            {/* Billetera - Hidden for business users */}
            {!isBusinessUser &&
            <Link
              to="/university-wallet"
              onClick={(e) => handleProtectedRouteClick(e, '/university-wallet')}
              className={`px-4 py-2 rounded-xl font-medium transition-all duration-200 ${
              user ?
              "text-gray-700 hover:text-blue-600 hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100" : "text-gray-400 hover:text-red-600 hover:bg-gradient-to-r hover:from-red-50 hover:to-red-100 cursor-pointer"}`
              }
              title={!user ? "Inicia sesión para acceder a la Billetera" : "Billetera"}>

                Billetera {!user && "🔒"}
              </Link>
            }
            
            {/* Impacto - Hidden for business users */}
            {!isBusinessUser &&
            <Link
              to="/impact-tracking"
              onClick={(e) => handleProtectedRouteClick(e, '/impact-tracking')}
              className={`px-4 py-2 rounded-xl font-medium transition-all duration-200 ${
              user ?
              "text-gray-700 hover:text-blue-600 hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100" : "text-gray-400 hover:text-red-600 hover:bg-gradient-to-r hover:from-red-50 hover:to-red-100 cursor-pointer"}`
              }
              title={!user ? "Inicia sesión para acceder al Impacto" : "Impacto"}>

                Impacto {!user && "🔒"}
              </Link>
            }
          </nav>

          <div className="flex items-center space-x-3">
            {/* QR Scanner Quick Access - Only for authenticated users */}
            {user &&
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/qr-scanner-screen')}
              className="text-gray-600 hover:text-white hover:bg-gradient-to-r hover:from-green-500 hover:to-green-600 transition-all duration-200 rounded-xl p-3"
              title="Escanear QR">

                <QrCode className="h-5 w-5" />
              </Button>
            }

            {/* User authentication section */}
            {!loading &&
            <>
                {user ?
              // Authenticated user menu
              <div className="flex items-center space-x-3">
                    <div className="hidden sm:flex items-center space-x-2 px-3 py-2 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl">
                      {isBusinessUser ?
                  <>
                          <img
                      src="https://images.unsplash.com/photo-1689475299375-b6b45ca0c56b"
                      alt="Cafe Central - Coffee shop"
                      className="w-8 h-8 rounded-full object-cover ring-2 ring-amber-200" />
                          <span className="text-sm text-gray-700 font-medium">
                            Cafe Central
                          </span>
                        </> :

                  <>
                          <img
                      src="https://img.rocket.new/generatedImages/rocket_gen_img_186db23cf-1762273709951.png"
                      alt="Foto de perfil de Sofia"
                      className="w-8 h-8 rounded-full object-cover ring-2 ring-green-200" />
                          <span className="text-sm text-gray-700 font-medium">
                            Sofia
                          </span>
                        </>
                  }
                    </div>
                    <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSignOut}
                  className="text-gray-600 hover:text-white hover:bg-gradient-to-r hover:from-red-500 hover:to-red-600 transition-all duration-200 rounded-xl p-3"
                  title="Cerrar Sesión">

                      <LogOut className="h-4 w-4" />
                    </Button>
                  </div> :

              // Login and Register buttons for non-authenticated users - Always visible when not loading
              <div className="flex items-center space-x-2">
                    <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/signin')}
                  className="flex items-center space-x-2 text-gray-600 hover:text-green-600 hover:bg-gradient-to-r hover:from-green-50 hover:to-green-100 transition-all duration-200 rounded-xl px-4 py-2 font-medium">

                      <LogIn className="h-4 w-4" />
                      <span>Iniciar Sesión</span>
                    </Button>
                    <Button
                  size="sm"
                  onClick={() => navigate('/signup')}
                  className="flex items-center space-x-2 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white transition-all duration-200 rounded-xl px-4 py-2 font-medium shadow-md">

                      <UserPlus className="h-4 w-4" />
                      <span>Registrarse</span>
                    </Button>
                  </div>
              }
              </>
            }
          </div>
        </div>
      </div>
    </header>);

};

export default Header;